import { FontFamilyValuesMap, BreakpointValuesMap, PaletteValuesMap, LightColorValuesMap, ElevationValuesMap, LightShadowsValuesMap, BorderWidthValuesMap, FontSizeValuesMap, LineHeightValuesMap, LetterSpacingValuesMap, SizeValuesMap, DarkColorValuesMap, DarkShadowsValuesMap } from '@kui-shared/tokens';
import { createStitches, css as css$1 } from '@stitches/core';

const FontFamilyWithFallbackValuesMap = {
  nvidiaSans: `"${FontFamilyValuesMap.nvidiaSans}", "${FontFamilyValuesMap.nvidiaSans} Fallback"`,
  jetbrainsMono: `"${FontFamilyValuesMap.jetbrainsMono}", "${FontFamilyValuesMap.jetbrainsMono} Fallback"`
};
const Breakpoints = {
  xxl: `(max-width: ${BreakpointValuesMap.xxl})`,
  xl: `(max-width: ${BreakpointValuesMap.xl})`,
  lg: `(max-width: ${BreakpointValuesMap.lg})`,
  md: `(max-width: ${BreakpointValuesMap.md})`,
  sm: `(max-width: ${BreakpointValuesMap.sm})`,
  xs: `(max-width: ${BreakpointValuesMap.xs})`
};
const {
  config,
  createTheme,
  css,
  globalCss,
  getCssText,
  reset,
  theme
} = createStitches({
  media: Breakpoints,
  theme: {
    colors: Object.assign(Object.assign({}, PaletteValuesMap), LightColorValuesMap),
    shadows: Object.assign(Object.assign({}, ElevationValuesMap), LightShadowsValuesMap),
    borderWidths: BorderWidthValuesMap,
    fonts: FontFamilyWithFallbackValuesMap,
    fontWeights: {
      bold: 700,
      light: 300,
      medium: 500,
      regular: 400
    },
    fontSizes: FontSizeValuesMap,
    lineHeights: LineHeightValuesMap,
    letterSpacings: LetterSpacingValuesMap,
    space: SizeValuesMap
  }
});

const FormFieldSizes = ['compact', 'standard', 'large'];
const FormFieldStatuses = ['invalid', 'valid'];
const getFormFieldStatusBorderCss = status => {
  const color = status === 'invalid' ? 'inputBorderError' : 'inputBorderActive';
  return {
    // https://stackoverflow.com/a/65726006
    borderImage: `linear-gradient(to top, ${theme.colors[color]}, ${theme.colors[color]} 2px, transparent 2px, transparent) 2`
  };
};

const getNvidiaFontUrl = format => `url("https://brand-assets.cne.ngc.nvidia.com/assets/fonts/nvidia-sans/1.0.0/NVIDIASans_${format}.woff2") format("woff2")`;
const FontDisplayFallback = 'fallback';
const FontStyleNormal = 'normal';
const FontStyleItalic = 'italic';
const FontWeightLight = 300;
const FontWeightRegular = 400;
const FontWeightMedium = 500;
const FontWeightBold = 700;
const FontFamilyNvidiaSans = `"${FontFamilyValuesMap.nvidiaSans}"`;
const FontFamilyNvidiaSansFallback = `"${FontFamilyValuesMap.nvidiaSans} Fallback"`;
const FontFamilyJetbrainsMonoFallback = `"${FontFamilyValuesMap.jetbrainsMono} Fallback"`;
const GlobalFontFace = globalCss({
  '@font-face': [{
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightLight,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('Lt')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightLight,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('LtIt')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightRegular,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('Rg')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightRegular,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('It')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightMedium,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('Md')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightMedium,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('MdIt')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightBold,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('Bd')
  }, {
    fontFamily: FontFamilyNvidiaSans,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightBold,
    fontDisplay: FontDisplayFallback,
    src: getNvidiaFontUrl('BdIt')
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightLight,
    src: "local('Courier New Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightRegular,
    src: "local('Courier New Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightMedium,
    src: "local('Courier New Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightBold,
    src: "local('Courier New Bold Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightLight,
    src: "local('Courier New')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightRegular,
    src: "local('Courier New')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightMedium,
    src: "local('Courier New')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyJetbrainsMonoFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightBold,
    src: "local('Courier New Bold')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '102.02%',
    descentOverride: '30.00%',
    lineGapOverride: '0.00%',
    sizeAdjust: '99.98%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightLight,
    src: "local('Arial Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '97.18%',
    descentOverride: '27.77%',
    lineGapOverride: '0.00%',
    sizeAdjust: '100.84%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightRegular,
    src: "local('Arial Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '96.18%',
    descentOverride: '27.48%',
    lineGapOverride: '0.00%',
    sizeAdjust: '101.89%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightMedium,
    src: "local('Arial Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '94.81%',
    descentOverride: '27.09%',
    lineGapOverride: '0.00%',
    sizeAdjust: '103.36%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleItalic,
    fontWeight: FontWeightBold,
    src: "local('Arial Bold Italic')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '101.13%',
    descentOverride: '28.90%',
    lineGapOverride: '0.00%',
    sizeAdjust: '96.90%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightLight,
    src: "local('Arial')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '93.59%',
    descentOverride: '26.74%',
    lineGapOverride: '0.00%',
    sizeAdjust: '104.71%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightRegular,
    src: "local('Arial')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '92.70%',
    descentOverride: '26.49%',
    lineGapOverride: '0.00%',
    sizeAdjust: '105.71%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightMedium,
    src: "local('Arial')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '91.53%',
    descentOverride: '26.15%',
    lineGapOverride: '0.00%',
    sizeAdjust: '107.07%'
  }, {
    fontFamily: FontFamilyNvidiaSansFallback,
    fontStyle: FontStyleNormal,
    fontWeight: FontWeightBold,
    src: "local('Arial Bold')",
    // @ts-expect-error Stitches does not support these properties in their types
    ascentOverride: '97.74%',
    descentOverride: '27.93%',
    lineGapOverride: '0.00%',
    sizeAdjust: '100.26%'
  }]
});
const GlobalReset = globalCss({
  '*': {
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    MozOsxFontSmoothing: 'grayscale',
    WebkitFontSmoothing: 'antialiased'
  }
});

/**
 * Please keep in order from top most zIndex to bottom
 *
 * important behaviors of zIndex property for consideration.
 * - a child with a higher zIndex than it's parent cannot exceed the parent zIndex.
 * - zIndex will ONLY be applied to positions other than sticky.
 * - go read the docs.
 */
const TOOLTIP_LAYER = 1080;
const POPOVER_LAYER = 1070;
const MENU_LAYER = 1060;
const MODAL_LAYER = 1050;
const OVERLAY_LAYER = 1040;
const SIDE_PANEL_LAYER = 1030;

// NOTE: Defines the alignment along the cross axis.
// @see: https://css-tricks.com/snippets/css/a-guide-to-flexbox/#aa-align-items
const AlignItemsValues = ['baseline', 'center', 'flex-end', 'flex-start', 'stretch'
// NOTE: unused for now, the majority of use cases are captured above.
// 'first baseline',
// 'last baseline',
// 'start',
// 'end',
// 'self-start',
// 'self-end',
];
// const alignContentValues = [] as const
// NOTE: Defines the alignment along the main axis.
// @see: https://css-tricks.com/snippets/css/a-guide-to-flexbox/#aa-justify-content
const JustifyContentValues = ['center', 'flex-end', 'flex-start', 'space-around', 'space-between', 'space-evenly'
// NOTE: unused for now, the majority of use cases are captured above.
// 'start',
// 'end',
// 'left',
// 'right',
];
// @see https://css-tricks.com/snippets/css/a-guide-to-flexbox/#aa-flex-direction
const DirectionValues = ['column', 'column-reverse', 'row', 'row-reverse'];
// @see https://css-tricks.com/snippets/css/a-guide-to-flexbox/#aa-flex-direction
const WrapValues = ['nowrap', 'wrap', 'wrap-reverse'];

const LightTheme = createTheme({
  colors: Object.assign(Object.assign({}, PaletteValuesMap), LightColorValuesMap),
  shadows: Object.assign(Object.assign({}, ElevationValuesMap), LightShadowsValuesMap)
});
const DarkTheme = createTheme({
  colors: Object.assign(Object.assign({}, PaletteValuesMap), DarkColorValuesMap),
  shadows: Object.assign(Object.assign({}, ElevationValuesMap), DarkShadowsValuesMap)
});
const ThemeColorValues = Object.keys(theme.colors);
const ThemeFontSizeValues = Object.keys(theme.fontSizes);
const ThemeSpaceValues = Object.keys(theme.space);

function createVariantMap(array, callback) {
  const result = {};
  for (const item of array) {
    result[item] = callback(item);
  }
  return result;
}
const EmptyStyle = css$1({});

export { AlignItemsValues, Breakpoints, DarkTheme, DirectionValues, EmptyStyle, FormFieldSizes, FormFieldStatuses, GlobalFontFace, GlobalReset, JustifyContentValues, LightTheme, MENU_LAYER, MODAL_LAYER, OVERLAY_LAYER, POPOVER_LAYER, SIDE_PANEL_LAYER, TOOLTIP_LAYER, ThemeColorValues, ThemeFontSizeValues, ThemeSpaceValues, WrapValues, config, createTheme, createVariantMap, css, getCssText, getFormFieldStatusBorderCss, globalCss, reset, theme };
